import React from 'react';
import { faPenToSquare, faPlus } from '@fortawesome/free-solid-svg-icons';

import { TranslationKeys } from 'app/translations';
import {
  getDisplayCurrencyValue,
  getDisplayDateValue,
  getDisplayOptionalValue,
  getDisplayPercentValue,
  getDisplayUTCDateValue,
  getDisplayYesOrNoValue,
  isNil,
} from 'app/utils';

import type { EmployeeEmployment, UseQueryRefetch } from 'app/types';

import {
  useContractTypeSelectItems,
  useEndOfEmploymentReasonSelectItems,
  useEmploymentEmployeeCategorySelectItems,
  useDialog,
  useEmploymentTypeSelectItems,
} from 'app/hooks';

import ActionButton from 'app/components/ActionButton';
import DetailsPanel from 'app/components/DetailsPanel';
import { useLocale } from 'app/components/Intl';

import { EmployeeDetailsPanelHeaderWithSyncToggle } from 'app/Domain/Employees/Pages/EmployeeDetailPage/components/EmployeeDetailsPanelHeaderWithSyncToggle';

import { EmployeeEmploymentDetailsFields } from './EmployeeEmploymentDetails.types';
import { EmployeeEmploymentFormDialog } from './components';
import { Box } from '@mui/material';

import { EMPLOYER, CONNECTION } from 'app/common/Authorization/entities';
import { EDIT, RUN } from 'app/common/Authorization/permissions';
import { HasAccessTo } from 'app/components';

type EmployeeEmploymentDetailsProps = {
  loading: boolean;
  isRegularConnection: boolean;
  refetchGetEmploymentsQuery: UseQueryRefetch;
  connectionId: string | undefined;
  employeeId: string | undefined;
  employment: EmployeeEmployment | null;
  synchronizationStatus: boolean | undefined;
  refetchGetEmployeeSynchronizationQuery: UseQueryRefetch;
  disabledActionButtonsWhileLoading: boolean;
};

export const EmployeeEmploymentDetails = ({
  connectionId,
  employeeId,
  employment,
  loading,
  isRegularConnection,
  refetchGetEmploymentsQuery,
  synchronizationStatus,
  refetchGetEmployeeSynchronizationQuery,
  disabledActionButtonsWhileLoading,
}: EmployeeEmploymentDetailsProps) => {
  const { locale } = useLocale();
  const { getEmploymentTypeDisplayNameByValue } = useEmploymentTypeSelectItems();
  const { getContractTypeDisplayNameByValue } = useContractTypeSelectItems();
  const { getEndOfEmploymentReasonDisplayNameByValue } = useEndOfEmploymentReasonSelectItems();
  const { getEmploymentEmployeeCategoryDisplayNameByValue } = useEmploymentEmployeeCategorySelectItems();
  const disabledIfNoAccessToEmployerEdit = !HasAccessTo(EMPLOYER, EDIT);
  const disabledIfNoAccessToConnectionRun = !HasAccessTo(CONNECTION, RUN);
  const hasActiveSynchronization = synchronizationStatus !== undefined ? synchronizationStatus : false;

  const {
    dialogState: employmentFormDialogState,
    openDialog: openEmploymentFormDialog,
    closeDialog: closeEmploymentFormDialog,
  } = useDialog();

  const [employmentToEdit, setEmploymentToEdit] = React.useState<EmployeeEmployment | null>(null);
  const shouldRenderEmployeeEmploymentFormDialog = !!connectionId && !!employeeId && employmentFormDialogState;

  const onOpenEmploymentFormDialog = React.useCallback(
    (employment: EmployeeEmployment | null) => {
      setEmploymentToEdit(employment);
      openEmploymentFormDialog();
    },
    [openEmploymentFormDialog],
  );

  const onCloseScheduleFormDialog = React.useCallback(() => {
    setEmploymentToEdit(null);
    closeEmploymentFormDialog();
  }, [closeEmploymentFormDialog]);

  const onClickAddButton = React.useCallback(() => {
    onOpenEmploymentFormDialog(null);
  }, [onOpenEmploymentFormDialog]);

  const onClickEditButton = React.useCallback(() => {
    onOpenEmploymentFormDialog(employment);
  }, [employment, onOpenEmploymentFormDialog]);

  const DetailsPanelColumns = React.useMemo(() => {
    const rows = {
      [EmployeeEmploymentDetailsFields.SequenceEmploymentId.Label]: employment?.sequenceEmploymentId ?? '-',
      [EmployeeEmploymentDetailsFields.StartDate.Label]: getDisplayDateValue(employment?.startDate),
      [EmployeeEmploymentDetailsFields.YearsFrom.Label]: getDisplayDateValue(employment?.yearsFrom),
      [EmployeeEmploymentDetailsFields.EndDate.Label]: getDisplayUTCDateValue(employment?.endDate),
      [EmployeeEmploymentDetailsFields.ProbationDate.Label]: getDisplayDateValue(employment?.probationDate),
      [EmployeeEmploymentDetailsFields.EmploymentType.Label]: !isNil(employment?.employmentType)
        ? getEmploymentTypeDisplayNameByValue(employment?.employmentType!)
        : '-',
      [EmployeeEmploymentDetailsFields.JobDescription.Label]: getDisplayOptionalValue(employment?.jobDescription),
      [EmployeeEmploymentDetailsFields.DeductionPlan.Label]: getDisplayOptionalValue(employment?.deductionPlan),
      [EmployeeEmploymentDetailsFields.NoRiskPolicyApplies.Label]: getDisplayYesOrNoValue(
        employment?.noRiskPolicyApplies,
      ),
      [EmployeeEmploymentDetailsFields.Emoluments.Label]: getDisplayCurrencyValue({
        locale,
        value: employment?.emoluments,
        options: EmployeeEmploymentDetailsFields.Emoluments.Options,
      }),
      [EmployeeEmploymentDetailsFields.VacationPay.Label]: getDisplayYesOrNoValue(employment?.vacationPay),
      [EmployeeEmploymentDetailsFields.VacationPayPercentage.Label]: getDisplayPercentValue({
        locale,
        value: employment?.vacationPayPercentage,
        options: EmployeeEmploymentDetailsFields.VacationPayPercentage.Options,
      }),
      [EmployeeEmploymentDetailsFields.ExtraMonth.Label]: getDisplayYesOrNoValue(employment?.extraMonth),
      [EmployeeEmploymentDetailsFields.OtherWageComponents.Label]: getDisplayYesOrNoValue(
        employment?.otherWageComponents,
      ),
      [EmployeeEmploymentDetailsFields.CalculationStartDate.Label]: getDisplayDateValue(
        employment?.calculationStartDate,
      ),
      [EmployeeEmploymentDetailsFields.ContractType.Label]: !isNil(employment?.contractType)
        ? getContractTypeDisplayNameByValue(employment?.contractType!)
        : '-',
      [EmployeeEmploymentDetailsFields.StartDateContractOfIndefiniteDuration.Label]: getDisplayDateValue(
        employment?.startDateContractOfIndefiniteDuration,
      ),
      [EmployeeEmploymentDetailsFields.CommissionUntilDate.Label]: getDisplayDateValue(employment?.commissionUntilDate),
      [EmployeeEmploymentDetailsFields.EndOfEmploymentReason.Label]: !isNil(employment?.endOfEmploymentReason)
        ? getEndOfEmploymentReasonDisplayNameByValue(employment?.endOfEmploymentReason!)
        : '-',
      [EmployeeEmploymentDetailsFields.EmployeeCategory.Label]:
        employment?.employeeCategory &&
        Array.isArray(employment.employeeCategory) &&
        employment.employeeCategory.length > 0
          ? employment.employeeCategory
              .map(category => getEmploymentEmployeeCategoryDisplayNameByValue(category))
              .join(', ')
          : '-',
    };

    return [{ rows }];
  }, [
    employment?.sequenceEmploymentId,
    employment?.startDate,
    employment?.yearsFrom,
    employment?.endDate,
    employment?.probationDate,
    employment?.employmentType,
    employment?.jobDescription,
    employment?.deductionPlan,
    employment?.noRiskPolicyApplies,
    employment?.emoluments,
    employment?.vacationPay,
    employment?.vacationPayPercentage,
    employment?.extraMonth,
    employment?.otherWageComponents,
    employment?.calculationStartDate,
    employment?.contractType,
    employment?.startDateContractOfIndefiniteDuration,
    employment?.commissionUntilDate,
    employment?.endOfEmploymentReason,
    employment?.employeeCategory,
    getEmploymentTypeDisplayNameByValue,
    getContractTypeDisplayNameByValue,
    getEndOfEmploymentReasonDisplayNameByValue,
    getEmploymentEmployeeCategoryDisplayNameByValue,
    locale,
  ]);

  const DetailsPanelHeader = React.useMemo(() => {
    return (
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
        <EmployeeDetailsPanelHeaderWithSyncToggle
          field={'employment'}
          loading={loading}
          synchronizationStatus={synchronizationStatus}
          refetchGetEmployeeSynchronizationQuery={refetchGetEmployeeSynchronizationQuery}
          disabledActionButtonsWhileLoading={disabledActionButtonsWhileLoading}
          messageId={TranslationKeys.employees_detail_employment}
          employeeId={employeeId}
          connectionId={connectionId}
        />
        {isRegularConnection && (
          <Box display="flex" gap={1}>
            <ActionButton
              messageId={TranslationKeys.employees_actions_editEmployment}
              variant="text"
              icon={faPenToSquare}
              onClick={onClickEditButton}
              disabled={
                disabledActionButtonsWhileLoading ||
                hasActiveSynchronization ||
                disabledIfNoAccessToEmployerEdit ||
                disabledIfNoAccessToConnectionRun
              }
            />
            <ActionButton
              messageId={TranslationKeys.employees_actions_AddEmployment}
              variant="text"
              icon={faPlus}
              onClick={onClickAddButton}
              disabled={
                disabledActionButtonsWhileLoading ||
                hasActiveSynchronization ||
                disabledIfNoAccessToEmployerEdit ||
                disabledIfNoAccessToConnectionRun
              }
            />
          </Box>
        )}
      </Box>
    );
  }, [
    disabledActionButtonsWhileLoading,
    connectionId,
    employeeId,
    loading,
    refetchGetEmployeeSynchronizationQuery,
    synchronizationStatus,
    isRegularConnection,
    onClickEditButton,
    onClickAddButton,
    hasActiveSynchronization,
    disabledIfNoAccessToEmployerEdit,
    disabledIfNoAccessToConnectionRun,
  ]);

  return (
    <>
      {shouldRenderEmployeeEmploymentFormDialog && (
        <EmployeeEmploymentFormDialog
          open={employmentFormDialogState}
          closeDialog={onCloseScheduleFormDialog}
          connectionId={connectionId}
          employeeId={employeeId}
          employment={employmentToEdit}
          refetchGetEmploymentsQuery={refetchGetEmploymentsQuery}
        />
      )}

      <DetailsPanel
        header={DetailsPanelHeader}
        columnsContainerSx={{ minHeight: '60em' }}
        columns={DetailsPanelColumns}
        loading={loading}
      />
    </>
  );
};
